import { Box, Group, Switch, Text } from "@mantine/core";
import classNames from "classnames";
import { useEffect, useId, useState } from "react";
import {
  useController,
  type Control,
  type FieldErrors,
  type FieldPath,
  type FieldValues
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { FormInputData } from "../../../Controllers/FormFieldController";
import { FormFieldController } from "../../../Controllers/FormFieldController";
import { CustomFormFieldWrapper } from "../../../MantineFormFields/CustomFormFieldWrapper/CustomFormFieldWrapper";
import { getErrorKeyFromFieldError } from "../../../MantineFormFields/HelperText/utils/getErrorKeyFromFieldError";
import { isFieldError } from "../../../MantineFormFields/HelperText/utils/isFieldError";
import "./DueDateFields.scss";

interface DueDateFieldsProps<
  T extends FieldValues,
  TName extends FieldPath<T>
> {
  allowNull?: boolean;
  control: Control<T>;
  errors: FieldErrors<T>;
  formFieldData: {
    [key in TName]: FormInputData<T, TName>;
  };
  label?: string;
  prefix?: string;
  unitFieldName: TName;
  valueFieldName: TName;
}

function DueDateFields<T extends FieldValues, TName extends FieldPath<T>>({
  allowNull,
  control,
  errors,
  formFieldData,
  label = "Fälligkeit",
  prefix,
  unitFieldName,
  valueFieldName
}: DueDateFieldsProps<T, TName>) {
  const { t } = useTranslation();
  const id = useId();
  const { field: valueFieldController } = useController({
    name: valueFieldName,
    control
  });
  const { field: unitFieldController } = useController({
    name: unitFieldName,
    control
  });
  const initialDueDateDisabled =
    valueFieldController.value === null && unitFieldController.value === null;

  const [isDueDateDisabled, setIsDueDateDisabled] = useState(
    initialDueDateDisabled
  );

  const valueError = isFieldError(errors[valueFieldName])
    ? errors[valueFieldName]
    : undefined;
  const unitError = isFieldError(errors[unitFieldName])
    ? errors[unitFieldName]
    : undefined;
  const dueDateErrorKey = getErrorKeyFromFieldError(valueError || unitError);
  const dueDateError = dueDateErrorKey && t(dueDateErrorKey);

  const required = isDueDateDisabled
    ? false
    : formFieldData[valueFieldName]?.required ||
      formFieldData[unitFieldName]?.required;

  const valueFieldData = { ...formFieldData[valueFieldName] };
  if (valueFieldData && valueFieldData.type === "number") {
    valueFieldData.id = id;
    valueFieldData.className = "due-date-value";
    valueFieldData.label = undefined;
    valueFieldData.disabled = isDueDateDisabled;
  }

  const unitFieldData = formFieldData[unitFieldName];
  if (unitFieldData && unitFieldData.type === "select") {
    unitFieldData.className = "due-date-unit";
    unitFieldData.label = undefined;
    unitFieldData.disabled = isDueDateDisabled;
  }

  function clearValue() {
    valueFieldController.onChange("");
    if (unitFieldData.type === "select") {
      unitFieldController.onChange(undefined);
    }
  }

  useEffect(() => {
    if (isDueDateDisabled) {
      valueFieldController.onChange(null);
      unitFieldController.onChange(null);
    }
  }, [isDueDateDisabled, valueFieldController, unitFieldController]);

  return (
    <Box
      className={classNames("DueDateFields", {
        "is-invalid": dueDateError
      })}
    >
      <CustomFormFieldWrapper
        error={dueDateError}
        id={id}
        label={label}
        required={required}
      >
        <Group align="center" gap="xs">
          {prefix && (
            <Text fw="inherit" span>
              {prefix}
            </Text>
          )}
          {valueFieldData && (
            <FormFieldController
              control={control}
              data={{ ...valueFieldData, required }}
            />
          )}
          {unitFieldData && (
            <FormFieldController
              control={control}
              data={{ ...unitFieldData, required }}
            />
          )}
          {allowNull && (
            <Switch
              checked={isDueDateDisabled}
              label={`Ohne ${label}`}
              onChange={(event) => {
                clearValue();
                setIsDueDateDisabled(event.currentTarget.checked);
              }}
            />
          )}
        </Group>
      </CustomFormFieldWrapper>
    </Box>
  );
}

export { DueDateFields, DueDateFieldsProps };
